/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { isEmpty } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiIcon, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyRegion } from '@modules/cluster-api/types'
import genericMessages from '@modules/project-lib/genericMessages'
import { getPlatformInfoById } from '@modules/utils/platform'

import type { FC } from 'react'

type Props = {
  regionId: string
  allRegions: AnyRegion[] | undefined
}

const FormattedRegionAndProvider: FC<Props> = ({ regionId, allRegions }) => {
  const region = allRegions?.find((item) => item.id === regionId)

  if (!region) {
    if (!isEmpty(regionId)) {
      return <EuiText size='s'>{regionId}</EuiText>
    }

    return (
      <EuiText size='s'>
        <FormattedMessage {...genericMessages.unknown} />
      </EuiText>
    )
  }

  return <EuiText size='s'>{`${region.csp.toUpperCase()} - ${region.name}`}</EuiText>
}

export const FormattedRegion: FC<Props> = ({ regionId, allRegions }) => {
  const region = allRegions?.find((item) => item.id === regionId)

  if (!region) {
    return <EuiText size='s'>{regionId}</EuiText>
  }

  return <EuiText size='s'>{region.name}</EuiText>
}

export const FormattedCloudProvider: FC<Props> = ({ regionId, allRegions }) => {
  const region = allRegions?.find((item) => item.id === regionId)

  if (!region) {
    return <EuiText size='s'>{regionId}</EuiText>
  }

  const platformInfo = getPlatformInfoById(region.csp)

  return (
    <EuiFlexGroup gutterSize='s' alignItems='center'>
      <EuiFlexItem grow={false}>
        <EuiIcon type={platformInfo.iconType} />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size='s'>{platformInfo.shortTitle}</EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default FormattedRegionAndProvider
